import React, { FC, PropsWithChildren, useEffect, useRef } from 'react';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { httpClient, httpClientDefaultHeaders } from '@/api/Core';
import { deployment } from '@/auth/config';
import { useIdTokenClaims } from '@/hooks/useIdTokenClaims';
import { LocalStorageKeys } from '@/utils/constants';
import { getTimezoneOffsetHours } from '@/utils/helpers';
import { HttpAuthContext } from './HttpAuthContext';

type SilentRequest = {
  authority: string;
  scopes: string[];
  account: ReturnType<IPublicClientApplication['getAllAccounts']>[number];
};

const httpAuthState = {
  cachedToken: null as AuthenticationResult | null,
  interceptorsRegistered: false,
  requestInterceptorId: undefined as number | undefined,
  responseInterceptorId: undefined as number | undefined,
  acquireToken: null as (() => Promise<string>) | null,
};

/** Used by SignalR `accessTokenFactory` (WebSockets + skipNegotiation → query access_token). */
export async function getAccessTokenForSignalR(): Promise<string> {
  if (httpAuthState.cachedToken?.accessToken) {
    const expiresOn = httpAuthState.cachedToken.expiresOn;
    if (!expiresOn || expiresOn.getTime() > Date.now() + 60_000) {
      return httpAuthState.cachedToken.accessToken;
    }
  }
  if (httpAuthState.acquireToken) {
    return httpAuthState.acquireToken();
  }
  throw new Error('SignalR access token is not available yet');
}

const buildSilentRequest = (
  instance: IPublicClientApplication,
  acr: string,
): SilentRequest => ({
  authority: `https://${deployment.b2cTenantName}.b2clogin.com/${deployment.b2cTenantId}/${acr}`,
  scopes: [
    'openid',
    'profile',
    `https://${deployment.b2cTenantName}.onmicrosoft.com/mtrest/User.Invite`,
    `https://${deployment.b2cTenantName}.onmicrosoft.com/mtrest/User.ReadAll`,
  ],
  account: instance.getAllAccounts()[0],
});

const ensureHttpInterceptors = (
  instance: IPublicClientApplication,
  getSilentRequest: () => SilentRequest,
) => {
  if (httpAuthState.interceptorsRegistered) return;

  httpAuthState.acquireToken = async () => {
    const token = await instance.acquireTokenSilent(getSilentRequest());
    httpAuthState.cachedToken = token;
    return token.accessToken;
  };

  httpAuthState.requestInterceptorId = httpClient.interceptors.request.use(
    async (config) => {
      let token = httpAuthState.cachedToken;

      if (!token) {
        try {
          token = await instance.acquireTokenSilent(getSilentRequest());
          httpAuthState.cachedToken = token;
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            return instance.acquireTokenRedirect(getSilentRequest());
          }
          throw error;
        }
      }

      return {
        ...config,
        headers: {
          ...config.headers,
          ...httpClientDefaultHeaders,
          Authorization: `Bearer ${token.accessToken}`,
          'X-App-Customers':
            localStorage.getItem(LocalStorageKeys.CustomerIds) || '',
          'X-App-Locations':
            localStorage.getItem(LocalStorageKeys.LocationIds) || '',
          'X-App-Timezone': getTimezoneOffsetHours(),
        },
      } as any;
    },
    (error) => Promise.reject(error),
  );

  httpAuthState.responseInterceptorId = httpClient.interceptors.response.use(
    (response) => {
      const errorRes = response as any;
      return (
        errorRes?.errorResponse
          ? {
              success: false,
              code: errorRes.code,
              message: errorRes.message,
            }
          : {
              success: true,
              code: response.status,
              data: response.data,
            }
      ) as any;
    },
    (err) => {
      const error = err?.response;
      const status = error?.status || 500;
      if (status === 401) {
        return instance.logout();
      }

      return {
        errorResponse: true,
        code: status,
        message: error?.data,
      };
    },
  );

  httpAuthState.interceptorsRegistered = true;
};

export const HttpInterceptor: FC<PropsWithChildren> = ({ children }) => {
  const silentRequestRef = useRef<SilentRequest | null>(null);
  const { instance, idTokenClaims } = useIdTokenClaims();
  const acr = idTokenClaims?.acr;

  // Register interceptors during render so they exist before child effects run.
  if (acr) {
    silentRequestRef.current = buildSilentRequest(instance, acr);
    ensureHttpInterceptors(instance, () => silentRequestRef.current!);
  } else {
    silentRequestRef.current = null;
    httpAuthState.cachedToken = null;
  }

  const isReady = Boolean(acr && httpAuthState.interceptorsRegistered);

  useEffect(() => {
    if (!acr || !silentRequestRef.current) return;

    void instance
      .acquireTokenSilent(silentRequestRef.current)
      .then((token) => {
        httpAuthState.cachedToken = token;
      })
      .catch((error) => {
        httpAuthState.cachedToken = null;
        if (error instanceof InteractionRequiredAuthError) {
          void instance.acquireTokenRedirect(silentRequestRef.current!);
        }
      });
  }, [acr, instance]);

  return (
    <HttpAuthContext.Provider value={{ isReady }}>
      {children as React.ReactElement}
    </HttpAuthContext.Provider>
  );
};
